import { ArrowBack } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Topbar from "../../../../components/topbar/TopBar";
import { getDepartmentListUseCase } from "../../department/domain/usecases/getDepartmentListUseCase";
import { createProvinceUseCase } from "../domain/usecases/createProvinceUseCase";

function createInitialForm() {
  return {
    departmentId: "",
    name: "",
    code: "",
    calculatePrice: false,
    reservation: false,
  };
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("created")
  );
}

export const ProvinceCreatePage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(createInitialForm);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const records = await getDepartmentListUseCase();
        setDepartments(records);

        if (records.length > 0) {
          setForm((prev) => ({
            ...prev,
            departmentId: String(records[0].id),
          }));
        }
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de departamentos.",
        });
      } finally {
        setLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "departmentId") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  const handleCheckChange = (event) => {
    const { name, checked } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSave = async () => {
    const payload = {
      departmentId: Number(form.departmentId),
      name: form.name.trim().toUpperCase(),
      code: form.code.trim().toUpperCase(),
      calculatePrice: Boolean(form.calculatePrice),
      reservation: Boolean(form.reservation),
    };

    try {
      setSaving(true);
      const response = await createProvinceUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la provincia.");
      }

      setMessage({
        open: true,
        type: "success",
        text: "Provincia creada correctamente.",
      });
      setForm({
        ...createInitialForm(),
        departmentId: departments[0] ? String(departments[0].id) : "",
      });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo crear la provincia.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      CREAR PROVINCIA
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      Registra el departamento, nombre, codigo y opciones de la
                      provincia.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    mx: "auto",
                    maxWidth: 480,
                    borderRadius: 3,
                    border: "1px solid rgba(20, 83, 45, 0.12)",
                    p: { xs: 1, md: 2 },
                  }}
                >
                  {loadingDepartments ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography color="text.secondary">
                        Cargando departamentos...
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Departamento"
                        name="departmentId"
                        value={form.departmentId}
                        onChange={handleChange}
                      >
                        {departments.map((department) => (
                          <MenuItem
                            key={department.id}
                            value={String(department.id)}
                          >
                            {department.name} - {department.code}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                      <TextField
                        fullWidth
                        label="Codigo"
                        name="code"
                        value={form.code}
                        onChange={handleChange}
                        inputProps={{ maxLength: 5 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.calculatePrice}
                            onChange={handleCheckChange}
                            name="calculatePrice"
                            sx={{
                              color: "#165c53",
                              "&.Mui-checked": {
                                color: "#165c53",
                              },
                            }}
                          />
                        }
                        label="Calcular Precio"
                        sx={{
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            color: "#000",
                            fontWeight: 600,
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.reservation}
                            onChange={handleCheckChange}
                            name="reservation"
                            sx={{
                              color: "#165c53",
                              "&.Mui-checked": {
                                color: "#165c53",
                              },
                            }}
                          />
                        }
                        label="Reserva"
                        sx={{
                          mx: 0,
                          "& .MuiFormControlLabel-label": {
                            color: "#000",
                            fontWeight: 600,
                          },
                        }}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || departments.length === 0}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving ? "Creando..." : "Crear"}
                      </Button>
                    </Stack>
                  )}
                </Paper>
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default ProvinceCreatePage;
